import { useState } from 'react'

type UseGroupToggleStateOptions = {
  /** 그룹의 기본 열림/닫힘 상태 (default: 'closed') */
  status?: 'open' | 'closed'
  /** 모든 그룹을 항상 열어둘지 여부 — true면 토글 무시 (default: false) */
  alwaysOpen?: boolean
  /** 검색 중일 때 모든 그룹을 강제로 열지 여부 (default: true) */
  forceOpenOnSearch?: boolean
  /** 현재 검색어 - forceOpenOnSearch와 함께 사용 */
  searchValue?: string
}

type UseGroupToggleStateReturn = {
  /** 특정 그룹의 열림 상태 토글 */
  toggle: (groupKey: string | number) => void
  /** 특정 그룹이 열려있는지 확인 */
  isOpen: (groupKey: string | number) => boolean
  /** 사용자 토글 이력 초기화 */
  reset: () => void
}

/**
 * 그룹의 열림/닫힘 상태를 관리하는 hook
 *
 * 사용자가 직접 토글한 그룹(toggledGroups)만 추적하고,
 * 나머지는 status 값에 따라 기본 상태를 반환한다.
 * → 외부 데이터(비동기 등)에 의존하지 않으므로 useEffect 동기화 불필요
 *
 * - status: 'closed' → 닫힘이 기본. toggledGroups에 있으면 열림
 * - status: 'open'   → 열림이 기본. toggledGroups에 있으면 닫힘
 *
 * @example
 * const leftToggle = useGroupToggleState({ status: 'closed', searchValue })
 * const rightToggle = useGroupToggleState({ status: 'open' })
 */
export const useGroupToggleState = (
  options: UseGroupToggleStateOptions = {},
): UseGroupToggleStateReturn => {
  const {
    status = 'closed',
    alwaysOpen = false,
    forceOpenOnSearch = true,
    searchValue = '',
  } = options
  const [toggledGroups, setToggledGroups] = useState<(string | number)[]>([])

  const toggle = (groupKey: string | number) => {
    setToggledGroups((prev) =>
      prev.includes(groupKey) ? prev.filter((k) => k !== groupKey) : [...prev, groupKey],
    )
  }

  const isOpen = (groupKey: string | number) => {
    if (alwaysOpen || (forceOpenOnSearch && searchValue !== '')) return true
    const isDefault = !toggledGroups.includes(groupKey)
    return status === 'open' ? isDefault : !isDefault
  }

  const reset = () => {
    setToggledGroups([])
  }
  return { toggle, isOpen, reset }
}

export default useGroupToggleState
